import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useGraphQLClient } from "./client";

export { QueryClientProvider, useQuery, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

/** 캐시 유지 시간 : 30분 */
const CACHE_GC_TIME = 1000 * 60 * 30;

export const INFINITE_LIST_QUERY_OPTIONS = {
  staleTime: Infinity, // Apollo의 `fetchPolicy: "cache-first"` 대응 (staleTime: Infinity → 재마운트 시 재요청 없이 캐시 사용)
  gcTime: CACHE_GC_TIME,
} as const;

/** Apollo `fetchPolicy: "no-cache"` 대응 (매번 새로 조회, 캐시에 남기지 않음) */
export const NO_CACHE_QUERY_OPTIONS = {
  staleTime: 0,
  gcTime: 0,
} as const;

/**
 * Apollo의 **기본** fetchPolicy("cache-first")를 재현하는 요청 함수.
 *
 * 명시적 fetchPolicy가 없던 useLazyQuery들은 Apollo에서 cache-first로 동작해
 * 같은 variables면 네트워크 요청 없이 캐시에서 즉시 반환됐다.
 * 명령형 `fetchX(variables)` API를 유지하면서 그 동작을 살리기 위해 사용한다.
 * (매번 최신이 필요한 no-cache 쿼리에는 사용하지 않는다)
 */
export function useCacheFirstRequest() {
  const request = useGraphQLClient();
  const queryClient = useQueryClient();

  return useCallback(
    <TData>(cacheKey: string, query: string, variables?: Record<string, unknown>) =>
      queryClient.fetchQuery({
        queryKey: ["shopifyV2", cacheKey, variables ?? null],
        queryFn: () => request<TData>(query, variables),
        staleTime: Infinity,
        gcTime: CACHE_GC_TIME,
      }),
    [request, queryClient],
  );
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false, // Apollo는 포커스 시 자동 refetch 하지 않음
        refetchOnReconnect: false, // Apollo는 재연결 시 자동 refetch 하지 않음
        retry: 0, // Apollo 기본 동작(재시도 없음)과 동일
      },
      mutations: {
        retry: 0,
      },
    },
  });
}
